import React, { useEffect, useRef, useState } from 'react';
import Pagination from 'components/Pagination/Pagination';
import { MTGCard } from 'types/interfaces/mtgCard';
import { CardSize } from 'components/CardSizeSelector/CardSizeSelector';
import { useIsMobile } from 'hooks/useIsMobile';
import ProgressBar from 'components/ProgressBar';
import ResultCard from './ResultCard';
import './ResultsGrid.css';

interface ResultsGridProps {
  cards: MTGCard[];
  cardSize: CardSize;
  /**
   * True whenever the rows on screen do not answer the filters that are set —
   * including the debounce window before a request has even been issued. Computed
   * once in SearchWorkbenchPage so the grid and the toolbar cannot disagree.
   */
  isResultsPending: boolean;
  isFetching: boolean;
  error: unknown;
  /** 1-based page on screen and the page size the request used (Asana 1219353862024235). */
  page: number;
  pageSize: number;
  /** Href for a page of the same search; the pager renders real links. */
  getPageHref: (page: number) => string;
  /** Cards view: a tile stands for every printing, so a click opens the Versions page. */
  onActivateCard?: (card: MTGCard) => void;
  /** Serialized request. Changing it means a different result set entirely. */
  queryKey: string;
  onClearAll: () => void;
  /** Server-side total, for the progress rail. Undefined until the first page lands. */
  totalCount?: number;
  /**
   * The trade filters as they stood for THIS result set. The grid needs them for
   * one reason: a zero-result page must be able to say which trade filter removed
   * the card, rather than leaving it to look like the card does not exist.
   */
  availableToSend?: boolean;
  availableToReceive?: boolean;
  /** Turns both trade filters off, leaving every other filter alone. */
  onClearTradeFilters?: () => void;
}

export const ResultsGrid: React.FC<ResultsGridProps> = ({
  cards,
  cardSize,
  isResultsPending,
  isFetching,
  error,
  page,
  pageSize,
  getPageHref,
  onActivateCard,
  queryKey,
  onClearAll,
  totalCount,
  availableToSend = false,
  availableToReceive = false,
  onClearTradeFilters,
}) => {
  const isMobile = useIsMobile();
  const [hoveredCard, setHoveredCard] = useState<string | null>(null);
  // One menu at a time: the grid owns which card has it open, so opening one
  // necessarily closes any other. Keyed by grid position, not scryfallId — the
  // same printing can legitimately appear twice in a result set, and keying on
  // the id alone opens every duplicate's menu at once.
  const [openMenuKey, setOpenMenuKey] = useState<string | null>(null);
  const gridRef = useRef<HTMLDivElement | null>(null);

  // A filter or page change is a new result set: drop the hover and any open
  // menu. Scrolling is the page's business (scroll-to-top on a page step,
  // restoration on the way back).
  useEffect(() => {
    setHoveredCard(null);
    setOpenMenuKey(null);
  }, [queryKey]);

  if (error) {
    return (
      <div className="results-empty">
        <p>Something went wrong loading results.</p>
        <p className="results-empty-hint">Please try again in a moment.</p>
      </div>
    );
  }

  // Pending comes before the zero-result branch on purpose. It used to be keyed on
  // isLoading, which is false during the debounce window, so an empty `cards` there
  // fell through and rendered "No cards match these filters" on essentially every
  // filter change — before a request had even been sent.
  if (isResultsPending) {
    return (
      <>
        <div className="results-loading">
          <ProgressBar label="Loading results" />
        </div>
        <div className={`results-grid size-${cardSize.toLowerCase()}`} aria-busy={isResultsPending}>
          {Array.from({ length: 10 }, (_, i) => (
            <div key={i} className="result-card-skeleton" />
          ))}
        </div>
      </>
    );
  }

  if (cards.length === 0) {
    // A trade filter is the one filter that removes a card for a reason the user
    // cannot see on the card itself, so it gets named explicitly. Without this a
    // searched-for card just silently is not there.
    const tradeExplanation = availableToSend && availableToReceive
      ? 'Available to send and Available to receive are both on, so only cards someone wants and someone offers are shown.'
      : availableToSend
        ? 'Available to send is on, so only cards someone wants are shown.'
        : availableToReceive
          ? 'Available to receive is on, so only cards someone offers for trade are shown.'
          : null;

    const clearTradeLabel = availableToSend && availableToReceive
      ? 'Turn off both trade filters'
      : availableToSend
        ? 'Turn off Available to send'
        : 'Turn off Available to receive';

    return (
      <div className="results-empty">
        <p>No cards match these filters.</p>
        {tradeExplanation && <p className="results-empty-hint">{tradeExplanation}</p>}
        <div className="results-empty-actions">
          {tradeExplanation && onClearTradeFilters && (
            <button type="button" className="results-empty-clear" onClick={onClearTradeFilters}>
              {clearTradeLabel}
            </button>
          )}
          <button type="button" className="results-empty-clear" onClick={onClearAll}>
            Clear filters
          </button>
        </div>
      </div>
    );
  }

  // Never let the footer claim fewer results than are already on screen.
  const total = totalCount === undefined ? undefined : Math.max(totalCount, cards.length);
  const totalPages = total === undefined ? 0 : Math.ceil(total / pageSize);
  const firstIndex = (page - 1) * pageSize + 1;
  const lastIndex = firstIndex + cards.length - 1;

  return (
    <>
      <div
        ref={gridRef}
        className={`results-grid size-${cardSize.toLowerCase()}`}
        aria-busy={isResultsPending}
      >
        {cards.map((card, index) => {
          const scryfallId =
            (card as unknown as { scryfallId?: string }).scryfallId ?? card.scryfallID;
          // Position-scoped so a printing appearing twice in one result set
          // hovers and opens its menu independently per tile.
          const cellKey = `${scryfallId}-${index}`;
          return (
            <ResultCard
              key={cellKey}
              card={card}
              isHovered={hoveredCard === cellKey}
              onHover={() => setHoveredCard(cellKey)}
              onLeave={() => setHoveredCard(null)}
              isMobile={isMobile}
              isMenuOpen={openMenuKey === cellKey}
              onOpenMenu={() => {
                // Opening a menu clears hover elsewhere, so two cards are never
                // both showing state at once.
                setOpenMenuKey(cellKey);
                setHoveredCard(cellKey);
              }}
              onCloseMenu={() => setOpenMenuKey(null)}
              onActivate={onActivateCard ? () => onActivateCard(card) : undefined}
            />
          );
        })}
      </div>

      {total !== undefined && total > 0 && (
        <div className="results-pagination" data-testid="results-pagination">
          <div className="results-pagination-status">
            Showing {firstIndex.toLocaleString()}–{lastIndex.toLocaleString()} of {total.toLocaleString()}
          </div>
          {totalPages > 1 && (
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              getPageHref={getPageHref}
              showFirstLast={false}
              compact={isMobile}
            />
          )}
        </div>
      )}
    </>
  );
};

export default ResultsGrid;
